import { useEffect } from "react";
import { createAndResizeCircImgDivs } from "../../scripts/resizer";
import mtaLogo from "../../assets/images/mtalogo_bl.svg";
import economicGrowthLogo from "../../assets/images/economicgrowth_cir.png";
import affordabilityLogo from "../../assets/images/affordability_cir.png";
import cleanAirLogo from "../../assets/images/cleanair_cir.png";
import goals1 from "../../assets/images/goals1_icon.svg";
import goals2 from "../../assets/images/goals2_icon.svg";
import goals3 from "../../assets/images/goals3_icon.svg";
import capitalplanIcon from "../../assets/images/captialplan_icon.svg";
import tynaIcon from "../../assets/images/20YNA_icon.svg";
import footerBottom from "../../assets/images/mtalogo_wt.svg";
import styles from "./CapitalPlan.module.css";

function CapitalPlan() {
	document.title = "MTA 2025-2029 Capital Plan";
	useEffect(() => {
		createAndResizeCircImgDivs(); // Call the function to resize images
	}, []);

	function smoothScrollToTop(duration) {
		const start = window.pageYOffset;
		const startTime =
			"now" in window.performance
				? performance.now()
				: new Date().getTime();

		const documentHeight = Math.max(
			document.body.scrollHeight,
			document.documentElement.scrollHeight,
			document.body.offsetHeight,
			document.documentElement.offsetHeight,
			document.body.clientHeight,
			document.documentElement.clientHeight
		);
		const windowHeight =
			window.innerHeight ||
			document.documentElement.clientHeight ||
			document.getElementsByTagName("body")[0].clientHeight;
		const destinationOffset = 0;
		const destinationOffsetToScroll = Math.round(
			documentHeight - destinationOffset < windowHeight
				? documentHeight - windowHeight
				: destinationOffset
		);

		if ("requestAnimationFrame" in window === false) {
			window.scroll(0, destinationOffsetToScroll);
			return;
		}

		function scroll() {
			const now =
				"now" in window.performance
					? performance.now()
					: new Date().getTime();
			const time = Math.min(1, (now - startTime) / duration);
			const timeFunction = time * (2 - time);
			window.scroll(
				0,
				Math.ceil(
					timeFunction * (destinationOffsetToScroll - start) + start
				)
			);

			if (window.pageYOffset === destinationOffsetToScroll) {
				return;
			}

			requestAnimationFrame(scroll);
		}

		scroll();
	}

	return (
		<div className={styles.capitalplan}>
			<div className={styles.container}>
				<header className={styles.header}>
					<img src={mtaLogo} alt="Metropolitan Transit Authority" />

					<div>
						<span>The future rides with us</span>
						<h1>2025-2029 Capital Plan</h1>
						<p>
							Transit makes New York possible. With investments
							like this Capital Plan, we make transit possible.
						</p>
						<a
							href="https://www.mta.info/document/174186"
							target="_blank"
							rel="noreferrer">
							<span></span> Download the plan
						</a>
					</div>

					<a href="#content" id={styles.skiptocontent}>
						Skip to main content
					</a>
				</header>

				<main className={styles.content}>
					<section className={`${styles.row} ${styles.intro}`}>
						<div className="col-md-12">
							<h2>There is no New York without mass transit</h2>
							<p>
								Our extensive network of trains and buses makes
								possible the greater density of people and jobs
								anywhere in the country. Great public
								transportation supports some of the nation's
								most iconic companies, hundreds of thousands of
								small businesses, world-class universities,
								hospitals, and cultural institutions. It also
								makes possible the walkable neighborhoods, local
								shops, vibrant street life, and great open
								spaces that make New York the greatest city on
								earth.
							</p>
							<div className={styles.row}>
								<div className="col-sm-4">
									<img
										src={economicGrowthLogo}
										alt=""
										aria-hidden="true"
									/>
									<h3>Economic growth</h3>
									<p>
										An effective transit system is essential
										for businesses, large and small, to draw
										employees and customers from the entire
										region. And millions of residents of the
										region depend on trains and buses to get
										to work and other destinations every
										day.
									</p>
								</div>
								<div className="col-sm-4">
									<img
										src={affordabilityLogo}
										alt=""
										aria-hidden="true"
									/>
									<h3>Affordability and opportunity</h3>
									<p>
										It's thanks to the MTA that New York
										City households have, on average, the
										lowest transportation costs in the
										nation.
									</p>
								</div>
								<div className="col-sm-4">
									<img
										src={cleanAirLogo}
										alt=""
										aria-hidden="true"
									/>
									<h3>
										Clean air and environmental
										sustainability
									</h3>
									<p>
										Transit is the best antidote to climate
										change. MTA subways, buses, and trains
										help avoid over 36 million vehicle miles
										being driven on an average weekday—about
										10.2 billion miles a year.
									</p>
								</div>
							</div>
						</div>
					</section>

					<section className={`${styles.row} ${styles.goals}`}>
						<div className="col-md-12">
							<h2>2025-2029 Capital Plan goals</h2>
							<div className={styles.row}>
								<div
									className={`col-sm-6 ${styles.goalImage}`}></div>
								<div className="col-sm-6">
									<img
										src={goals1}
										alt=""
										aria-hidden="true"
									/>
									<h3>
										Provide frequent and reliable service
									</h3>
									<ul>
										<li>
											Install at least 75 miles of modern
											signals on the Broadway{" "}
											<span
												className={`${styles.metroLine} ${styles.lineNqrw}`}>
												N
											</span>
											<span
												className={`${styles.metroLine} ${styles.lineNqrw}`}>
												R
											</span>
											<span
												className={`${styles.metroLine} ${styles.lineNqrw}`}>
												Q
											</span>
											<span
												className={`${styles.metroLine} ${styles.lineNqrw}`}>
												W
											</span>{" "}
											Line, Liberty Av and Rockaway{" "}
											<span
												className={`${styles.metroLine} ${styles.lineAce}`}>
												A
											</span>
											<span
												className={`${styles.metroLine} ${styles.lineS}`}>
												S
											</span>{" "}
											Lines, and the Nassau St{" "}
											<span
												className={`${styles.metroLine} ${styles.lineJz}`}>
												J
											</span>
											<span
												className={`${styles.metroLine} ${styles.lineJz}`}>
												Z
											</span>{" "}
											Line to improve reliability and
											frequency of service.
										</li>
										<li>
											Order 2,000 new railcars to reduce
											delays. New cars are six times more
											reliable than older ones.
										</li>
										<li>
											Modernize train shops and yards,
											including the 100-year old Livonia
											Shop, to accommodate new rolling
											stock and accelerate repair times.
										</li>
									</ul>
									<p>
										<strong>... and more</strong>
									</p>
								</div>
							</div>
							<div className={styles.row}>
								<div
									className={`col-sm-6 ${styles.goalImage}`}></div>
								<div className="col-sm-6">
									<img
										src={goals2}
										alt=""
										aria-hidden="true"
									/>
									<h3>Improve the customer experience</h3>
									<ul>
										<li>
											Make at least 60 more subway
											stations accessible, bringing the
											system to greater than 50%
											accessibility, and serving nearly
											70% of our riders.
										</li>
										<li>
											Repair or rehabilitate more than 150
											subway stations and more than 25
											railroad stations.
										</li>
										<li>
											Install new fare gate systems in
											more than 150 subway stations to
											increase fare collection and improve
											accessibility.
										</li>
									</ul>
									<p>
										<strong>... and more</strong>
									</p>
								</div>
							</div>
							<div className={styles.row}>
								<div
									className={`col-sm-6 ${styles.goalImage}`}></div>
								<div className="col-sm-6">
									<img
										src={goals3}
										alt=""
										aria-hidden="true"
									/>
									<h3>Take action on climate change</h3>
									<ul>
										<li>
											Purchase 500 zero-emissions buses
											and install charging infrastructure
											at bus depots, with special emphasis
											on communities with high air
											pollution and asthma rates.
										</li>
										<li>
											Install new infrastructure to
											improve stormwater flood protection
											at our 20 most vulnerable stations
											and yards.
										</li>
										<li>
											Protect 20 miles of the Hudson Line
											that are the most vulnerable to
											sea-level rise and stormwater
											runoff.
										</li>
									</ul>
									<p>
										<strong>... and more</strong>
									</p>
								</div>
							</div>
						</div>
					</section>
					<section
						className={`${styles.row} ${styles["get-involved"]}`}>
						<div className="col-md-12">
							<h2>Get involved</h2>
							<h3>Share feedback</h3>
							<p>
								Help us select stations for accessibility
								upgrades:
							</p>
							<a
								href="https://www.mta.info/accessibility/select-stations"
								className={`${styles["signoff-link"]} ${styles["no-m"]}`}
								target="_blank"
								rel="noreferrer">
								Share your input
							</a>
							<h3>Upcoming events</h3>
							<ul>
								<li>No upcoming events.</li>
							</ul>
							<h3>Past events</h3>
							<ul>
								<li>
									Open House for the 2025 - 2029 Capital Plan
									| February 20, 5-7 p.m. | White Plains
									Public Library, 100 Martine Ave
								</li>
								<li>
									Open House for the 2025 - 2029 Capital Plan
									| October 21, 2024 from 3p.m.-6p.m. |
									Vanderbilt Hall in Grand Central Terminal
								</li>
								<li>
									Open House for the 2025 - 2029 Capital Plan
									| November 13, 2024 from 4p.m. - 7p.m. |
									Queens Public Library at Central (89-11
									Merrick Blvd, Jamaica)
								</li>
								<li>
									Open House for the 2025 - 2029 Capital Plan
									| November 21, 2024 from 4p.m. - 7p.m. |
									Brooklyn Borough Hall
								</li>
								<li>
									Open House for the 2025 - 2029 Capital Plan
									| December 16, 3-6 p.m. | Bronx Library
									Center, 310 E Kingsbridge Rd
								</li>
							</ul>
						</div>
					</section>

					<section className={`${styles.row} ${styles.resources}`}>
						<div className="col-md-12">
							<h2>Resources</h2>
							<ul>
								<li>
									<a
										href="https://www.mta.info/document/174186"
										target="_blank"
										rel="noreferrer">
										Full 2025-2029 Capital Plan Report{" "}
										<em>May 2025</em>
										<span></span>
									</a>
								</li>
								<li>
									<a
										href="https://new.mta.info/document/151301"
										target="_blank"
										rel="noreferrer">
										Planned Investments in New York City{" "}
										<em>May 2025</em>
										<span></span>
									</a>
								</li>
								<li>
									<a
										href="https://new.mta.info/document/151296"
										target="_blank"
										rel="noreferrer">
										Planned Investments Throughout the
										Region <em>May 2025</em>
										<span></span>
									</a>
								</li>
							</ul>
							<a
								href="https://new.mta.info/agency/construction-and-development"
								className={styles["signoff-link"]}
								target="_blank"
								rel="noreferrer">
								Looking for more information on MTA's Capital
								Program? Visit our Construction & Development
								page.
							</a>
						</div>
					</section>
				</main>

				<footer className={styles.footer}>
					<div id={styles["footer-top"]}>
						<h2>Explore our Capital Program</h2>
						<div>
							<a href="#top" rel="noreferrer">
								<img
									src={capitalplanIcon}
									alt=""
									aria-hidden="true"
								/>
								<h3>
									2025-2029 <span>Capital Plan</span>
								</h3>
								<p>
									Building on progress made in the past four
									decades to modernize and strengthen the
									network.
								</p>
							</a>
							<a
								href="https://future.mta.info/"
								target="_blank"
								rel="noreferrer">
								<img src={tynaIcon} alt="" aria-hidden="true" />
								<h3>
									20-Year Needs <span>Assessment</span>
								</h3>
								<p>
									A broad, comprehensive blueprint that
									outlines the MTA region's transportation
									capital needs for the next generation
									(2025-2044).
								</p>
							</a>
						</div>
					</div>
					<div id={styles["footer-bottom"]}>
						<img
							src={footerBottom}
							alt="Metropolitan Transit Authority"
						/>
						<p>2025-2029 Capital Plan | The future rides with us</p>
						<a
							href="#top"
							onClick={(e) => {
								e.preventDefault();
								smoothScrollToTop(900); // 2000 milliseconds = 2 seconds
							}}>
							<span className={styles["ada-element"]}>
								Back to Top
							</span>
						</a>
					</div>
				</footer>
			</div>
		</div>
	);
}

export default CapitalPlan;
